import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../client';
import { PortableText } from '@portabletext/react';

// Reusable Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
  </div>
);

// Reusable Error Message
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again.'}</p>
  </div>
);

// Portable Text component configuration for links
const ptComponents = {
  marks: {
    link: ({ children, value }) => {
      if (!value?.href) {
        return <span>{children}</span>;
      }
      const rel = !value.href.startsWith('/') ? 'noopener noreferrer' : undefined;
      const target = (value.blank || value.href.startsWith('http')) ? '_blank' : undefined;
      return (
        <a href={value.href} className="text-[#691e31] hover:underline" rel={rel} target={target}>
          {children}
        </a>
      );
    }
  },
};

const ParentCoordinatorCorner = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        const query = `*[_type == "parentCoordinatorPage" && _id == "parentCoordinatorPageContent"][0] {
          bannerTitle,
          bannerImage,
          welcomeTitle,
          body,
          signatureName,
          signatureTitle
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('Page content not found.');
        }
      } catch (err) {
        console.error("Failed to fetch Parent Coordinator page data:", err);
        setError('Failed to load content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  const bannerImageUrl = pageData.bannerImage ? urlFor(pageData.bannerImage).url() : '/images/parent coordinator corner/parent-coordinator.jpg';

  return (
    <div className="bg-white">
      {/* Hero Banner Section */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          <img 
            src={bannerImageUrl}
            alt={pageData.bannerTitle || 'Welcome to the Parent Coordinator Corner'} 
            className="absolute inset-0 w-full h-full object-cover object-center" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
            <div className="prose max-w-none text-gray-700 leading-relaxed space-y-4">
              <h2 className="text-2xl font-bold text-[#2a4865] mb-4">
                {pageData.welcomeTitle}
              </h2>

              {pageData.body && (
                <PortableText 
                  value={pageData.body}
                  components={ptComponents}
                />
              )}
            </div>

            <div className="mt-10 pt-6 border-t border-gray-200 text-gray-800">
              <p className="font-semibold">Warm Regards,</p>
              <p className="text-2xl italic text-[#691e31] my-2" style={{ fontFamily: '"Great Vibes", cursive, sans-serif' }}>{pageData.signatureName}</p>
              <p className="font-semibold">{pageData.signatureTitle}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParentCoordinatorCorner; 